import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LazyMediaDirective } from '../../shared/directives/lazy-media.directive';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { inject } from '@angular/core';
import { UserReactionService } from '../../shared/services/user-reaction.service';
// import { ReactionSoundService } from '../../shared/services/reaction-sound.service';
import { RelationshipService } from '../../shared/services/relationship.service';
import { FriendRequestService, FriendshipStatus } from '../../shared/services/friend-request.service';
import { PostService, ReactionType } from '../../shared/services/post.service';
import { EventService } from '../../shared/services/event.service';
import { BlogService } from '../../shared/services/blog.service';
import { Modal } from '../../components/ui/modal/modal';
import { ButtonComponent } from '../../components/ui/button/button';
import { FriendRequestLimitPopup } from '../../components/ui/friend-request-limit-popup/friend-request-limit-popup';
import { ConfirmationPopup } from '../../components/ui/confirmation-popup/confirmation-popup';

// ── Shared reaction config ────────────────────────────────────────────────────
export const REACTIONS: {
  type: ReactionType; emoji: string; label: string; color: string; bg: string;
}[] = [
    { type: 'LIKE', emoji: '👍', label: 'Like', color: '#2078f4', bg: '#e7f3ff' },
    { type: 'HEART', emoji: '❤️', label: 'Love', color: '#f33e58', bg: '#ffe7eb' },
    { type: 'HUG', emoji: '🤗', label: 'Hug', color: '#f7b125', bg: '#fff4e0' },
    { type: 'WOW', emoji: '😮', label: 'Wow', color: '#f7b125', bg: '#fff4e0' },
    { type: 'SAD', emoji: '😢', label: 'Sad', color: '#7c93a3', bg: '#eef2f5' },
    { type: 'HAHA', emoji: '😂', label: 'Haha', color: '#f7b125', bg: '#fff4e0' },
  ];

interface ReactionCounts { LIKE: number; HEART: number; HUG: number; WOW: number; SAD: number; HAHA: number; }

// ── Unified card that holds typed sub-VM ─────────────────────────────────────
export interface ReactionCard {
  reactionId: string;
  reactionType: ReactionType;
  reactedAt: string;
  contentType: 'POST' | 'EVENT' | 'BLOG';
  post?: PostVM;
  event?: EventVM;
  blog?: BlogVM;
}

export interface PostVM {
  id: string;
  hostName: string; hostAvatar: string; hostId: string;
  timeAgo: string;
  images: { url: string; type: 'image' | 'video' }[];
  activeSlide: number;
  title: string; description: string; expanded: boolean;
  myReaction: ReactionType | null;
  reactions: number; comments: number; shares: number; totalReactions: number; likes: number;
  isMyPostedPost: boolean; isFollowed: boolean;
  friendshipStatus: FriendshipStatus; friendshipRequestId: string | null;
  location?: string; hashtags: string[];
  backgroundPattern?: string; backgroundBg?: string; hasBackground: boolean;
  showEmojiPicker: boolean; hoveredReaction: ReactionType | null;
  reactionCounts: ReactionCounts; mentions: any[]; collaborations: any[];
  acceptedCollaborators: Array<{ _id: string; displayName: string; userName: string; profileImage?: { urlSmall?: string } }>;
}

export interface EventVM {
  id: string;
  hostName: string; hostAvatar: string; hostId: string; hostGender: 'f' | 'm';
  timeAgo: string; createdAt: string;
  images: string[]; activeSlide: number;
  title: string; startDateLabel: string; endDateLabel: string; startDateISO: string;
  location: string; linkToBookingSite: string | null;
  minPrice: number; maxPrice: number;
  likes: number; comments: number; shares: number; totalReactions: number;
  myReaction: ReactionType | null; reactionCounts: ReactionCounts;
  showEmojiPicker: boolean; hoveredReaction: ReactionType | null;
  isMyPostedEvent: boolean; isFollowed: boolean;
  friendshipStatus: FriendshipStatus; friendshipRequestId: string | null;
  acceptedCollaborators: Array<{ _id: string; displayName: string; userName: string; profileImage?: { urlSmall?: string } }>;
}

export interface BlogVM {
  _id: string; userId: string;
  avatar: string; author: string; title: string; timeAgo: string;
  images: string[]; mediaBlocks: any[]; activeSlide: number; imageAspect: string;
  text: string; hasMore: boolean;
  likes: number; comments: number; shares: number;
  myReaction: ReactionType | null; reactions: number; totalReactions: number; reactionCounts: ReactionCounts;
  showEmojiPicker: boolean; hoveredReaction: ReactionType | null;
  isFollowed: boolean; isMyPostedBlog: boolean;
  friendshipStatus: FriendshipStatus; friendshipRequestId: string | null;
  acceptedCollaborators: Array<{ _id: string; displayName: string; userName: string; profileImage?: { urlSmall?: string; urlMedium?: string; urlLarge?: string } | null }>;
}

// ── Component ─────────────────────────────────────────────────────────────────
@Component({
  selector: 'app-user-reactions',
  standalone: true,
  imports: [CommonModule, Modal, ButtonComponent, LazyMediaDirective, FriendRequestLimitPopup, ConfirmationPopup],
  templateUrl: './user-reactions.html',
})
export class UserReactions implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() userName = '';
  @ViewChildren('cardRef') cardRefs!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('loadMoreSentinel') loadMoreSentinelRef?: ElementRef<HTMLElement>;

  readonly REACTIONS = REACTIONS;

  cards: ReactionCard[] = [];
  isLoading = false;
  hasError = false;
  page = 1;
  limit = 9;
  total = 0;
  allLoaded = false;

  // Reaction modal state (shared across all card types)
  isReactionOpen = false;
  reactionData: any = null;
  reactionsLoading = false;
  activeReactionFilter: ReactionType | null = null;
  private activeContentId: string | null = null;

  // Collab modal
  showCollabModal = false;
  selectedPost?: PostVM;
  selectedEvent?: EventVM;
  selectedBlog?: BlogVM;

  private resizeObserver?: ResizeObserver;
  private cardChangeSub?: Subscription;
  private intersectionObserver?: IntersectionObserver;

  private reactionSvc = inject(UserReactionService);
  // private soundSvc = inject(ReactionSoundService);
  private relationSvc = inject(RelationshipService);
  private friendSvc = inject(FriendRequestService);
  private postSvc = inject(PostService);
  private eventSvc = inject(EventService);
  private blogSvc = inject(BlogService);

  showFriendLimitPopup = false;
  friendLimitHours: number | null = null;

  showUnfriendConfirm = false;
  private pendingUnfriendAction: (() => void) | null = null;

  promptUnfriend(action: () => void): void {
    this.pendingUnfriendAction = action;
    this.showUnfriendConfirm = true;
  }

  confirmUnfriend(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction?.();
    this.pendingUnfriendAction = null;
  }

  cancelUnfriendConfirm(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction = null;
  }

  private handleFriendCooldown(err: any): void {
    const cooldownHours = this.friendSvc.parseCooldownError(err);
    if (cooldownHours !== null) {
      this.friendLimitHours = cooldownHours;
      this.showFriendLimitPopup = true;
    }
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) { }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  ngOnInit(): void { this.load(); }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userName'] && !changes['userName'].firstChange) {
      this.reset(); this.load();
    }
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.cardChangeSub = this.cardRefs.changes.subscribe(() =>
      this.zone.runOutsideAngular(() =>
        requestAnimationFrame(() => requestAnimationFrame(() => this.spanAll()))));
    this.resizeObserver = new ResizeObserver(() =>
      this.zone.runOutsideAngular(() =>
        requestAnimationFrame(() => requestAnimationFrame(() => this.spanAll()))));
    this.resizeObserver.observe(document.body);
    [50, 300, 800].forEach(d => setTimeout(() => {
      if (this.cardRefs?.length)
        this.zone.runOutsideAngular(() =>
          requestAnimationFrame(() => requestAnimationFrame(() => this.spanAll())));
    }, d));

    if (this.loadMoreSentinelRef) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !this.isLoading && !this.allLoaded) {
            this.zone.run(() => this.loadMore());
          }
        },
        { threshold: 0.1 },
      );
      this.intersectionObserver.observe(this.loadMoreSentinelRef.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.cardChangeSub?.unsubscribe();
  }

  // ── Data ─────────────────────────────────────────────────────────────────

  private reset(): void { this.page = 1; this.cards = []; this.total = 0; this.allLoaded = false; }

  load(): void {
    if (!this.userName || this.isLoading || this.allLoaded) return;
    this.isLoading = true;
    this.hasError = false;

    this.reactionSvc.getUserReactions(this.userName, this.page, this.limit).subscribe({
      next: (res: any) => {
        this.zone.run(() => {
          const raw = res?.data?.items ?? [];
          const mapped = raw.map((r: any) => this.toCard(r));
          this.cards = this.page === 1 ? mapped : [...this.cards, ...mapped];
          this.total = res?.data?.total ?? 0;

          if (this.cards.length >= this.total) {
            this.allLoaded = true;
          }

          this.isLoading = false;
          this.cdr.detectChanges();
          this.zone.runOutsideAngular(() =>
            requestAnimationFrame(() => requestAnimationFrame(() => this.spanAll())));
        });
      },
      error: () => this.zone.run(() => { this.isLoading = false; this.hasError = true; }),
    });
  }

  loadMore(): void {
    if (this.isLoading || this.allLoaded) return;
    this.page++;
    this.load();
  }

  get hasMorePages(): boolean { return !this.allLoaded; }

  // ── Map raw API item → ReactionCard ─────────────────────────────────────

  private toCard(raw: any): ReactionCard {
    const c = raw.content ?? {};
    const type = c.type as 'POST' | 'EVENT' | 'BLOG';

    const base: ReactionCard = {
      reactionId: raw.reactionId,
      reactionType: raw.reactionType as ReactionType,
      reactedAt: raw.reactedAt,
      contentType: type,
    };

    if (type === 'POST') { base.post = this.toPostVM(c); base.post.myReaction = base.reactionType; }
    if (type === 'EVENT') { base.event = this.toEventVM(c); base.event.myReaction = base.reactionType; }
    if (type === 'BLOG') { base.blog = this.toBlogVM(c); base.blog.myReaction = base.reactionType; }

    return base;
  }

  private toPostVM(c: any): PostVM {
    const user = c.user ?? {};
    const postInfo = c.postInfo ?? {};
    const avatar = user?.profileImage?.urlSmall ?? user?.profileImage?.urlMedium ??
      this.avatarFallback(user?.displayName);
    const images = (c.media ?? []).map((m: any) => ({
      url: m.urlMedium ?? m.url ?? '',
      type: m.type === 'video' ? 'video' as const : 'image' as const,
    }));
    const acceptedCollaborators = (postInfo?.collaborations ?? [])
      .filter((col: any) => col.status === 'ACCEPTED')
      .map((col: any) => ({ _id: col.user?._id ?? '', displayName: col.user?.displayName ?? '', userName: col.user?.userName ?? '', profileImage: col.user?.profileImage }));
    const rxn = c.reactions ?? {};
    const total = rxn.total ?? c.numberOfLikes ?? 0;
    return {
      id: c._id ?? '', hostName: user?.displayName ?? '', hostAvatar: avatar, hostId: user?._id ?? '',
      timeAgo: this.timeAgo(c.createdAt), images, activeSlide: 0,
      title: c.title ?? '', description: c.title ?? '', expanded: false,
      myReaction: rxn.myReaction ?? null, reactions: total, comments: c.numberOfComments ?? 0,
      shares: c.numberOfShares ?? 0, totalReactions: total, likes: total,
      isMyPostedPost: c.isMyPostedPost ?? false, isFollowed: c.isFollowed ?? false,
      friendshipStatus: (c.friendship?.status ?? 'NONE') as FriendshipStatus,
      friendshipRequestId: c.friendship?.requestId ?? null,
      location: postInfo?.location, hashtags: c.hashtags ?? [],
      backgroundPattern: postInfo?.backgroundPattern, backgroundBg: postInfo?.backgroundBg,
      hasBackground: !!(postInfo?.backgroundPattern || postInfo?.backgroundBg),
      showEmojiPicker: false, hoveredReaction: null,
      reactionCounts: rxn.counts ?? { LIKE: 0, HEART: 0, HUG: 0, WOW: 0, SAD: 0, HAHA: 0 },
      mentions: postInfo?.mentions ?? [], collaborations: postInfo?.collaborations ?? [],
      acceptedCollaborators,
    };
  }

  private toEventVM(c: any): EventVM {
    const user = c.user ?? {};
    const info = c.info ?? {};
    const avatar = user?.profileImage?.urlSmall ?? user?.profileImage?.urlMedium ??
      this.avatarFallback(user?.displayName);
    const images = (c.media ?? []).length
      ? (c.media ?? []).map((m: any) => m.urlMedium ?? m.url)
      : [c?.info?.logo].filter(Boolean);

    const acceptedCollaborators = (info?.collaborations ?? [])
      .filter((col: any) => col.status === 'ACCEPTED')
      .map((col: any) => ({ _id: col.user?._id ?? '', displayName: col.user?.displayName ?? '', userName: col.user?.userName ?? '', profileImage: col.user?.profileImage }));
    const rxn = c.reactions ?? {};
    const total = rxn.total ?? c.numberOfLikes ?? 0;
    return {
      id: c._id ?? '', hostName: user?.displayName ?? '', hostAvatar: avatar, hostId: user?._id ?? '',
      hostGender: 'f', timeAgo: this.timeAgo(c.createdAt), createdAt: c.createdAt ?? '',
      images, activeSlide: 0,
      title: info?.title ?? c.title ?? '',
      startDateLabel: info?.startDate ? new Date(info.startDate).toLocaleString() : '',
      endDateLabel: info?.endDate ? new Date(info.endDate).toLocaleString() : '',
      startDateISO: info?.startDate?.split('T')[0] ?? '',
      location: info?.location ?? '', linkToBookingSite: info?.linkToBookingSite ?? null,
      minPrice: Number(info?.priceMin ?? 0), maxPrice: Number(info?.priceMax ?? 0),
      likes: total, comments: c.numberOfComments ?? 0, shares: c.numberOfShares ?? 0, totalReactions: total,
      myReaction: rxn.myReaction ?? null,
      reactionCounts: rxn.counts ?? { LIKE: 0, HEART: 0, HUG: 0, WOW: 0, SAD: 0, HAHA: 0 },
      showEmojiPicker: false, hoveredReaction: null,
      isMyPostedEvent: c.isMyPostedEvent ?? false, isFollowed: c.isFollowed ?? false,
      friendshipStatus: (c.friendship?.status ?? 'NONE') as FriendshipStatus,
      friendshipRequestId: c.friendship?.requestId ?? null,
      acceptedCollaborators,
    };
  }

  private toBlogVM(c: any): BlogVM {
    const user = c.user ?? {};
    const blocks = (c.blogInfo?.blocks ?? []).slice().sort((a: any, b: any) => a.position - b.position);
    const mediaBlocks = blocks.filter((b: any) => b.type === 'IMAGE' || b.type === 'VIDEO');
    const images = mediaBlocks.map((b: any) => b.mediaId?.urlMedium ?? b.mediaId?.url ?? '').filter(Boolean);
    const firstText = blocks.find((b: any) => b.type === 'TEXT');
    const rawText = firstText?.text ? this.stripHtml(firstText.text) : '';
    const LIMIT = 200;
    const text = rawText.length > LIMIT ? rawText.slice(0, LIMIT).trimEnd() + '…' : rawText;
    const avatar = user?.profileImage?.urlSmall ?? user?.profileImage?.urlMedium ??
      this.avatarFallback(user?.displayName);
    const acceptedCollaborators = (c.blogInfo?.collaborations ?? [])
      .filter((col: any) => col.status === 'ACCEPTED')
      .map((col: any) => ({ _id: col.user?._id ?? '', displayName: col.user?.displayName ?? '', userName: col.user?.userName ?? '', profileImage: col.user?.profileImage }));
    const rxn = c.reactions ?? {};
    const total = rxn.total ?? c.numberOfLikes ?? 0;
    return {
      _id: c._id ?? '', userId: user?._id ?? '',
      avatar, author: user?.displayName ?? '', title: c.title ?? '', timeAgo: this.timeAgo(c.createdAt),
      images, mediaBlocks, activeSlide: 0,
      imageAspect: images.length >= 3 ? '3/4' : images.length === 2 ? '16/9' : '4/3',
      text, hasMore: rawText.length > LIMIT,
      likes: total, comments: c.numberOfComments ?? 0, shares: c.numberOfShares ?? 0,
      myReaction: rxn.myReaction ?? null, reactions: total, totalReactions: total,
      reactionCounts: rxn.counts ?? { LIKE: 0, HEART: 0, HUG: 0, WOW: 0, SAD: 0, HAHA: 0 },
      showEmojiPicker: false, hoveredReaction: null,
      isFollowed: c.isFollowed ?? false, isMyPostedBlog: c.isMyPostedBlog ?? false,
      friendshipStatus: (c.friendship?.status ?? 'NONE') as FriendshipStatus,
      friendshipRequestId: c.friendship?.requestId ?? null,
      acceptedCollaborators,
    };
  }

  // ── Navigation ───────────────────────────────────────────────────────────

  navigateToPost(id: string): void { this.router.navigate(['/post', id]); }
  navigateToEvent(id: string): void { this.router.navigate(['/event', id]); }
  navigateToBlog(id: string): void { this.router.navigate(['/blog', id]); }
  openBooking(link: string | null): void { if (link) window.open(link, '_blank'); }

  // ── Slide helpers ────────────────────────────────────────────────────────

  prevSlidePost(p: PostVM, e: MouseEvent): void { e.stopPropagation(); if (p.activeSlide > 0) { p.activeSlide--; this.spanAllDeferred(); } }
  nextSlidePost(p: PostVM, e: MouseEvent): void { e.stopPropagation(); if (p.activeSlide < p.images.length - 1) { p.activeSlide++; this.spanAllDeferred(); } }
  goToSlidePost(p: PostVM, i: number, e: MouseEvent): void { e.stopPropagation(); p.activeSlide = i; this.spanAllDeferred(); }

  prevSlideEvent(ev: EventVM, e: MouseEvent): void { e.stopPropagation(); if (ev.activeSlide > 0) { ev.activeSlide--; this.spanAllDeferred(); } }
  nextSlideEvent(ev: EventVM, e: MouseEvent): void { e.stopPropagation(); if (ev.activeSlide < ev.images.length - 1) { ev.activeSlide++; this.spanAllDeferred(); } }
  goToSlideEvent(ev: EventVM, i: number, e: MouseEvent): void { e.stopPropagation(); ev.activeSlide = i; this.spanAllDeferred(); }

  goToSlideBlog(b: BlogVM, i: number): void { b.activeSlide = i; this.spanAllDeferred(); }
  isVideoSlideBlog(b: BlogVM, i: number): boolean { return b.mediaBlocks[i]?.type === 'VIDEO'; }

  expandPost(p: PostVM): void { p.expanded = true; this.cdr.detectChanges(); this.spanAllDeferred(); }
  collapsePost(p: PostVM): void { p.expanded = false; this.cdr.detectChanges(); this.spanAllDeferred(); }

  private spanAllDeferred(): void {
    this.zone.runOutsideAngular(() =>
      requestAnimationFrame(() => requestAnimationFrame(() => this.spanAll())));
  }

  // ── getPatternClass (mirrors posts.ts exactly) ───────────────────────────

  getPatternClass(patternId?: string): string {
    if (!patternId) return '';
    const map: Record<string, string> = {
            'p-checks-purple': 'pattern-checks-md',
      'p-zigzag-sky': 'pattern-zigzag-md',
      'p-grid-green': 'pattern-grid-md', 'p-diag-orange': 'pattern-diagonal-lines-md',
      'p-hlines-teal': 'pattern-horizontal-lines-md', 'p-vstripe-rose': 'pattern-vertical-stripes-md',
      'p-crossdots-navy': 'pattern-cross-dots-lg', 'p-dstripe-amber': 'pattern-diagonal-stripes-md',
      'p-h-indigo': 'pattern-h-lg',
    };
    return map[patternId] || '';
  }

  getHighlightedCaption(text: string): string {
    if (!text) return '';
    const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    return escaped.replace(/@([\w._]+)/g, '<span style="color:#2563eb;font-weight:700;">@$1</span>');
  }

  // ── Reaction config helper ────────────────────────────────────────────────

  getReactionConfig(type: ReactionType | null) {
    if (!type) return null;
    return REACTIONS.find(r => r.type === type) ?? null;
  }

  friendButtonTitle(status: string): string {
    const map: Record<string, string> = {
      NONE: 'Send friend request', PENDING_SENT: 'Cancel friend request',
      PENDING_RECEIVED: 'Accept friend request', FRIENDS: 'Unfriend', BLOCKED: 'Blocked',
    };
    return map[status] ?? '';
  }

  // ── POST reactions ────────────────────────────────────────────────────────

  onReactionClickPost(p: PostVM): void {
    if (p.showEmojiPicker) { p.showEmojiPicker = false; return; }
    // this.soundSvc.playPickerOpen();
    this.cards.forEach(c => { if (c.post) c.post.showEmojiPicker = false; });
    p.showEmojiPicker = true;
    this.cdr.detectChanges();
  }
  onEmojiPickPost(p: PostVM, type: ReactionType, $e: MouseEvent): void {
    $e.stopPropagation(); p.showEmojiPicker = false; p.hoveredReaction = null;
    // this.soundSvc.playReactionSelect(type);
    this.submitReactionPost(p, type);
  }
  closeEmojiPickerPost(p: PostVM): void { p.showEmojiPicker = false; p.hoveredReaction = null; this.cdr.detectChanges(); }

  private submitReactionPost(p: PostVM, type: ReactionType): void {
    const prev = p.myReaction;
    if (prev === type) { p.reactionCounts[type] = Math.max(0, p.reactionCounts[type] - 1); p.totalReactions = Math.max(0, p.totalReactions - 1); p.myReaction = null; }
    else { if (prev) { p.reactionCounts[prev]--; p.totalReactions--; } p.reactionCounts[type]++; p.totalReactions++; p.myReaction = type; }
    p.reactions = p.totalReactions; p.likes = p.totalReactions; this.cdr.detectChanges();
    this.postSvc.reactToPost(p.id, type).subscribe({ error: () => { p.myReaction = prev; this.cdr.detectChanges(); } });
  }

  openReactionModalPost(p: PostVM): void { this.activeContentId = p.id; this.isReactionOpen = true; this.activeReactionFilter = null; this.loadReactionsFor(p.id); }

  // ── EVENT reactions ───────────────────────────────────────────────────────

  onReactionClickEvent(ev: EventVM): void {
    if (ev.showEmojiPicker) { ev.showEmojiPicker = false; return; }
    // this.soundSvc.playPickerOpen();
    this.cards.forEach(c => { if (c.event) c.event.showEmojiPicker = false; });
    ev.showEmojiPicker = true; this.cdr.detectChanges();
  }
  onEmojiPickEvent(ev: EventVM, type: ReactionType, $e: MouseEvent): void {
    $e.stopPropagation(); ev.showEmojiPicker = false; ev.hoveredReaction = null;
    // this.soundSvc.playReactionSelect(type);
    this.submitReactionEvent(ev, type);
  }
  closeEmojiPickerEvent(ev: EventVM): void { ev.showEmojiPicker = false; ev.hoveredReaction = null; this.cdr.detectChanges(); }

  private submitReactionEvent(ev: EventVM, type: ReactionType): void {
    const prev = ev.myReaction;
    if (prev === type) { ev.reactionCounts[type] = Math.max(0, ev.reactionCounts[type] - 1); ev.totalReactions = Math.max(0, ev.totalReactions - 1); ev.myReaction = null; }
    else { if (prev) { ev.reactionCounts[prev]--; ev.totalReactions--; } ev.reactionCounts[type]++; ev.totalReactions++; ev.myReaction = type; }
    ev.likes = ev.totalReactions; this.cdr.detectChanges();
    this.eventSvc.reactToEvent(ev.id, type).subscribe({ error: () => { ev.myReaction = prev; this.cdr.detectChanges(); } });
  }

  openReactionModalEvent(ev: EventVM): void { this.activeContentId = ev.id; this.isReactionOpen = true; this.activeReactionFilter = null; this.loadReactionsFor(ev.id); }

  // ── BLOG reactions ────────────────────────────────────────────────────────

  onReactionClickBlog(b: BlogVM): void {
    if (b.showEmojiPicker) { b.showEmojiPicker = false; return; }
    // this.soundSvc.playPickerOpen();
    this.cards.forEach(c => { if (c.blog) c.blog.showEmojiPicker = false; });
    b.showEmojiPicker = true; this.cdr.detectChanges();
  }
  onEmojiPickBlog(b: BlogVM, type: ReactionType, $e: MouseEvent): void {
    $e.stopPropagation(); b.showEmojiPicker = false; b.hoveredReaction = null;
    // this.soundSvc.playReactionSelect(type);
    this.submitReactionBlog(b, type);
  }
  closeEmojiPickerBlog(b: BlogVM): void { b.showEmojiPicker = false; b.hoveredReaction = null; this.cdr.detectChanges(); }

  private submitReactionBlog(b: BlogVM, type: ReactionType): void {
    const prev = b.myReaction;
    if (prev === type) { b.reactionCounts[type] = Math.max(0, b.reactionCounts[type] - 1); b.totalReactions = Math.max(0, b.totalReactions - 1); b.myReaction = null; }
    else { if (prev) { b.reactionCounts[prev]--; b.totalReactions--; } b.reactionCounts[type]++; b.totalReactions++; b.myReaction = type; }
    b.reactions = b.totalReactions; b.likes = b.totalReactions; this.cdr.detectChanges();
    this.blogSvc.reactToBlog(b._id, type).subscribe({ error: () => { b.myReaction = prev; this.cdr.detectChanges(); } });
  }

  openReactionModalBlog(b: BlogVM): void { this.activeContentId = b._id; this.isReactionOpen = true; this.activeReactionFilter = null; this.loadReactionsFor(b._id); }

  // ── Reaction modal shared ────────────────────────────────────────────────

  closeReactionModal(): void { this.isReactionOpen = false; this.reactionData = null; this.activeContentId = null; }

  setReactionFilter(type: ReactionType | null): void {
    this.activeReactionFilter = type;
    if (this.activeContentId) this.loadReactionsFor(this.activeContentId, type ?? undefined);
  }

  get filteredReactions(): any[] { return this.reactionData?.all ?? []; }

  private loadReactionsFor(contentId: string, type?: ReactionType): void {
    this.reactionsLoading = true; this.cdr.detectChanges();
    this.blogSvc.getReactions(contentId, type).subscribe({
      next: (res: any) => { this.reactionData = res?.data; this.reactionsLoading = false; this.cdr.detectChanges(); },
      error: () => { this.reactionsLoading = false; this.cdr.detectChanges(); },
    });
  }

  // ── Follow / Friend (Post) ────────────────────────────────────────────────

  followPost(e: MouseEvent, p: PostVM): void {
    e.stopPropagation(); p.isFollowed = true; this.cdr.detectChanges();
    this.relationSvc.follow(p.hostId).subscribe({ error: () => { p.isFollowed = false; this.cdr.detectChanges(); } });
  }
  unfollowPost(e: MouseEvent, p: PostVM): void {
    e.stopPropagation(); p.isFollowed = false; this.cdr.detectChanges();
    this.relationSvc.unfollow(p.hostId).subscribe({ error: () => { p.isFollowed = true; this.cdr.detectChanges(); } });
  }
  onFriendClickPost(p: PostVM, e: MouseEvent): void {
    e.stopPropagation();
    if (p.friendshipStatus === 'NONE') { this.sendFriendReq(p.hostId, p, e); }
    else if (p.friendshipStatus === 'PENDING_SENT') { this.cancelFriendReq(p, e); }
    else if (p.friendshipStatus === 'FRIENDS') { this.promptUnfriend(() => this.unfriendP(p.hostId, p, e)); }
  }

  // ── Follow / Friend (Event) ───────────────────────────────────────────────

  followEvent(e: MouseEvent, ev: EventVM): void {
    e.stopPropagation(); ev.isFollowed = true; this.cdr.detectChanges();
    this.relationSvc.follow(ev.hostId).subscribe({ error: () => { ev.isFollowed = false; this.cdr.detectChanges(); } });
  }
  unfollowEvent(e: MouseEvent, ev: EventVM): void {
    e.stopPropagation(); ev.isFollowed = false; this.cdr.detectChanges();
    this.relationSvc.unfollow(ev.hostId).subscribe({ error: () => { ev.isFollowed = true; this.cdr.detectChanges(); } });
  }
  onFriendClickEvent(ev: EventVM, e: MouseEvent): void {
    e.stopPropagation();
    if (ev.friendshipStatus === 'NONE') { this.sendFriendReqEv(ev.hostId, ev, e); }
    else if (ev.friendshipStatus === 'PENDING_SENT') { this.cancelFriendReqEv(ev, e); }
    else if (ev.friendshipStatus === 'FRIENDS') { this.promptUnfriend(() => this.unfriendEv(ev.hostId, ev, e)); }
  }

  // ── Follow / Friend (Blog) ────────────────────────────────────────────────

  followBlog(e: MouseEvent, b: BlogVM): void {
    e.stopPropagation(); b.isFollowed = true; this.cdr.detectChanges();
    this.relationSvc.follow(b.userId).subscribe({ error: () => { b.isFollowed = false; this.cdr.detectChanges(); } });
  }
  unfollowBlog(e: MouseEvent, b: BlogVM): void {
    e.stopPropagation(); b.isFollowed = false; this.cdr.detectChanges();
    this.relationSvc.unfollow(b.userId).subscribe({ error: () => { b.isFollowed = true; this.cdr.detectChanges(); } });
  }
  onFriendClickBlog(b: BlogVM, e: MouseEvent): void {
    e.stopPropagation();
    if (b.friendshipStatus === 'NONE') { this.sendFriendReqBl(b.userId, b, e); }
    else if (b.friendshipStatus === 'PENDING_SENT') { this.cancelFriendReqBl(b, e); }
    else if (b.friendshipStatus === 'FRIENDS') { this.promptUnfriend(() => this.unfriendBl(b.userId, b, e)); }
  }

  // ── Collab modal ──────────────────────────────────────────────────────────

  showCollabPost(p: PostVM): void { this.selectedPost = p; this.showCollabModal = true; }
  showCollabEvent(ev: EventVM): void { this.selectedEvent = ev; this.showCollabModal = true; }
  showCollabBlog(b: BlogVM): void { this.selectedBlog = b; this.showCollabModal = true; }
  closeCollabModal(): void { this.showCollabModal = false; this.selectedPost = undefined; this.selectedEvent = undefined; this.selectedBlog = undefined; }

  get collabAuthorAvatar(): string { return this.selectedPost?.hostAvatar ?? this.selectedEvent?.hostAvatar ?? this.selectedBlog?.avatar ?? ''; }
  get collabAuthorName(): string { return this.selectedPost?.hostName ?? this.selectedEvent?.hostName ?? this.selectedBlog?.author ?? ''; }
  get collabCollaborators(): any[] { return this.selectedPost?.acceptedCollaborators ?? this.selectedEvent?.acceptedCollaborators ?? this.selectedBlog?.acceptedCollaborators ?? []; }

  // ── Masonry ──────────────────────────────────────────────────────────────

  onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    const card = img.closest<HTMLElement>('.rxn-masonry-item');
    if (!card) return;
    this.zone.runOutsideAngular(() =>
      requestAnimationFrame(() => requestAnimationFrame(() => this.spanCard(card))));
  }

  private spanAll(): void { this.cardRefs?.forEach(ref => this.spanCard(ref.nativeElement)); }

  private spanCard(el: HTMLElement): void {
    const h = el.getBoundingClientRect().height;
    if (h === 0) { setTimeout(() => { const h2 = el.getBoundingClientRect().height; if (h2 > 0) el.style.gridRowEnd = `span ${Math.ceil((h2 + 20) / 10)}`; }, 100); return; }
    el.style.gridRowEnd = `span ${Math.ceil((h + 20) / 10)}`;
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  trackById(_: number, c: ReactionCard): string { return c.reactionId; }

  /** Single source of truth for the ui-avatars fallback used across this page. */
  avatarFallback(name?: string | null): string {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFD4E0&color=D31148`;
  }
  trackByIndex(i: number): number { return i; }

  /**
   * Plain-text preview for card snippets. Uses the same tag-to-space regex on
   * both server and client (rather than DOM textContent in the browser) —
   * textContent doesn't insert whitespace at tag boundaries, so block markup
   * like a pasted <table> collapses into one run-together word (e.g. every
   * <td> in a row glued to the next with no space). It would also produce
   * different output server- vs client-side, which is its own hydration risk.
   */
  private stripHtml(html: string): string {
    return html
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/\s+/g, ' ')
      .trim();
  }

  private timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m} minute${m > 1 ? 's' : ''} ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hour${h > 1 ? 's' : ''} ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d} day${d > 1 ? 's' : ''} ago`;
    const w = Math.floor(d / 7);
    if (w < 5) return `${w} week${w > 1 ? 's' : ''} ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo} month${mo > 1 ? 's' : ''} ago`;
    return `${Math.floor(mo / 12)} year${Math.floor(mo / 12) > 1 ? 's' : ''} ago`;
  }

  // ── Private friend helpers ────────────────────────────────────────────────

  private sendFriendReq(userId: string, p: PostVM, e: MouseEvent): void {
    e.stopPropagation(); p.friendshipStatus = 'PENDING_SENT'; this.cdr.detectChanges();
    this.friendSvc.send(userId).subscribe({ next: (res: any) => { p.friendshipRequestId = res?.data?._id ?? null; this.cdr.detectChanges(); }, error: (err: any) => { if ((err?.error?.statusCode ?? err?.status) !== 409) p.friendshipStatus = 'NONE'; this.handleFriendCooldown(err); this.cdr.detectChanges(); } });
  }
  private cancelFriendReq(p: PostVM, e: MouseEvent): void {
    e.stopPropagation(); if (!p.friendshipRequestId) return;
    const prev = p.friendshipStatus; const prevId = p.friendshipRequestId;
    p.friendshipStatus = 'NONE'; this.cdr.detectChanges();
    this.friendSvc.cancel(p.friendshipRequestId).subscribe({ error: () => { p.friendshipStatus = prev; p.friendshipRequestId = prevId; this.cdr.detectChanges(); } });
  }
  private unfriendP(userId: string, p: PostVM, e: MouseEvent): void {
    e.stopPropagation(); const prev = p.friendshipStatus; p.friendshipStatus = 'NONE'; this.cdr.detectChanges();
    this.friendSvc.unfriend(userId).subscribe({ error: () => { p.friendshipStatus = prev; this.cdr.detectChanges(); } });
  }

  private sendFriendReqEv(userId: string, ev: EventVM, e: MouseEvent): void {
    e.stopPropagation(); ev.friendshipStatus = 'PENDING_SENT'; this.cdr.detectChanges();
    this.friendSvc.send(userId).subscribe({ next: (res: any) => { ev.friendshipRequestId = res?.data?._id ?? null; this.cdr.detectChanges(); }, error: (err: any) => { if ((err?.error?.statusCode ?? err?.status) !== 409) ev.friendshipStatus = 'NONE'; this.handleFriendCooldown(err); this.cdr.detectChanges(); } });
  }
  private cancelFriendReqEv(ev: EventVM, e: MouseEvent): void {
    e.stopPropagation(); if (!ev.friendshipRequestId) return;
    const prev = ev.friendshipStatus; const prevId = ev.friendshipRequestId;
    ev.friendshipStatus = 'NONE'; this.cdr.detectChanges();
    this.friendSvc.cancel(ev.friendshipRequestId).subscribe({ error: () => { ev.friendshipStatus = prev; ev.friendshipRequestId = prevId; this.cdr.detectChanges(); } });
  }
  private unfriendEv(userId: string, ev: EventVM, e: MouseEvent): void {
    e.stopPropagation(); const prev = ev.friendshipStatus; ev.friendshipStatus = 'NONE'; this.cdr.detectChanges();
    this.friendSvc.unfriend(userId).subscribe({ error: () => { ev.friendshipStatus = prev; this.cdr.detectChanges(); } });
  }

  private sendFriendReqBl(userId: string, b: BlogVM, e: MouseEvent): void {
    e.stopPropagation(); b.friendshipStatus = 'PENDING_SENT'; this.cdr.detectChanges();
    this.friendSvc.send(userId).subscribe({ next: (res: any) => { b.friendshipRequestId = res?.data?._id ?? null; this.cdr.detectChanges(); }, error: (err: any) => { if ((err?.error?.statusCode ?? err?.status) !== 409) b.friendshipStatus = 'NONE'; this.handleFriendCooldown(err); this.cdr.detectChanges(); } });
  }
  private cancelFriendReqBl(b: BlogVM, e: MouseEvent): void {
    e.stopPropagation(); if (!b.friendshipRequestId) return;
    const prev = b.friendshipStatus; const prevId = b.friendshipRequestId;
    b.friendshipStatus = 'NONE'; this.cdr.detectChanges();
    this.friendSvc.cancel(b.friendshipRequestId).subscribe({ error: () => { b.friendshipStatus = prev; b.friendshipRequestId = prevId; this.cdr.detectChanges(); } });
  }
  private unfriendBl(userId: string, b: BlogVM, e: MouseEvent): void {
    e.stopPropagation(); const prev = b.friendshipStatus; b.friendshipStatus = 'NONE'; this.cdr.detectChanges();
    this.friendSvc.unfriend(userId).subscribe({ error: () => { b.friendshipStatus = prev; this.cdr.detectChanges(); } });
  }

  onModalFollowClick(item: any, event: MouseEvent): void {
    event.stopPropagation();
    if (item.isFollowed) {
      item.isFollowed = false;
      this.cdr.markForCheck();
      this.relationSvc.unfollow(item.user?._id).subscribe({
        error: () => { item.isFollowed = true; this.cdr.markForCheck(); }
      });
    } else {
      item.isFollowed = true;
      this.cdr.markForCheck();
      this.relationSvc.follow(item.user?._id).subscribe({
        error: () => { item.isFollowed = false; this.cdr.markForCheck(); }
      });
    }
  }

  onModalFriendClick(item: any, event: MouseEvent): void {
    event.stopPropagation();
    const status = item.friendship.status ?? 'NONE';

    if (status === 'NONE') {
      item.friendship.status = 'PENDING_SENT';
      this.cdr.markForCheck();
      this.friendSvc.send(item.user?._id).subscribe({
        next: (res: any) => { item.friendship.requestId = res?.data?._id ?? null; this.cdr.markForCheck(); },
        error: (err: any) => {
          const code = err?.error?.statusCode ?? err?.status;
          if (code !== 409) item.friendship.status = 'NONE';
          this.handleFriendCooldown(err);
          this.cdr.markForCheck();
        }
      });
    } else if (status === 'PENDING_SENT') {
      const prev = item.friendship.status;
      const prevId = item.friendship.requestId;
      item.friendship.status = 'NONE';
      this.cdr.markForCheck();
      this.friendSvc.cancel(item.friendship.requestId).subscribe({
        error: () => { item.friendship.status = prev; item.friendship.requestId = prevId; this.cdr.markForCheck(); }
      });
    } else if (status === 'FRIENDS') {
      this.promptUnfriend(() => {
        const prev = item.friendship.status;
        item.friendship.status = 'NONE';
        this.cdr.markForCheck();
        this.friendSvc.unfriend(item.user?._id).subscribe({
          error: () => { item.friendship.status = prev; this.cdr.markForCheck(); }
        });
      });
    }
  }
}
